<template>
    <div class="dd-rule-value-row">
        <div v-if="index !== 1 && !isHidden">
            <div class="comparison-label">
                {{ ruleObject.comparison }}
            </div>
        </div>

        <DsDatePicker
            v-if="isDatePicker"
            v-model="selectedDatePicker"
            date-only
            :label="$t('funnel.edit.decision.category.select.label')"
            class="ds-datepicker-wrapper"
            @input="onDatePickerInput"
        />
        <DsInputField
            v-if="isInputTextField"
            v-model="selectedInputTextField"
            class="ds-input-field-wrapper"
            :placeholder="$t('funnel.edit.decision.category.select.label')"
            @blur="onBlurSaveInputTextField"
        >
            <template #trailing>
                <span
                    class="icon-x-delete-rule-value-input"
                    @click="deleteRuleValueEvent"
                >
                    <DsIcon name="minus-circle" />
                </span>
            </template>
        </DsInputField>

        <DsMultiselect
            v-if="isMultiselectInputField"
            v-model="selectedMultiselectInputField"
            multiple
            searchable
            :allow-add="allowAdd"
            :add-text="$t('funnel.edit.decision.category.delete.field.label')"
            :placeholder="$t('funnel.edit.decision.category.select.label')"
            class="ds-multiselect-wrapper"
            :options="multiselectInputFieldOptions"
            @add="deleteRuleValueEvent"
        >
            <template #selection="{ option }">
                <DsChip
                    remove
                    with-interaction
                    @mousedown.native.stop.prevent="deleteRuleValueEvent"
                >
                    <template #default>
                        {{ `${option.label}` }}
                    </template>
                </DsChip>
            </template>

            <template #option="{ option, isSelected }">
                <div
                    class="menu-item"
                    :class="[{
                        selected: isSelected,
                        isDeleteOption: option.value === 'delete'
                    }]"
                >
                    <span v-if="option.groupLabel" class="group-label">{{ option.groupLabel }}</span>
                    <span v-else class="option-label">{{ option.label }}</span>
                </div>
            </template>
        </DsMultiselect>

        <div v-if="index == numRuleValues && (mayOrValue || mayAndValue)" class="may-or-and-wrapper">
            <span v-if="showOrValues" class="may-or-label" @click="addRuleValueEvent(COMPARISON_OPTIONS.OR)">
                {{ $t('funnel.edit.decision.plus.or.label') }}
            </span>
            <span v-if="showAndValues" class="may-and-label" @click="addRuleValueEvent(COMPARISON_OPTIONS.AND)">
                {{ $t('funnel.edit.decision.plus.and.label') }}
            </span>
        </div>
    </div>
</template>

<script>
export const COMPARISON_OPTIONS = {
    OR: 'Or',
    AND: 'And',
};
import {
    getInputAttributes,
    getAllUsers,
    addRuleValue,
    deleteRuleValue,
} from './decision.api';
import {
    convertFromDpToString,
    convertFromStringToDp,
    INPUT_TYPE_HIDDEN,
    INPUT_TYPE_AUTOCOMPLETE,
    INPUT_TYPE_DATE,
    INPUT_TYPE_CITY,
    INPUT_TYPE_STRING,
    INPUT_TYPE_NAME,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_PHONE,
    INPUT_TYPE_STATE,
    INPUT_TYPE_URL,
    INPUT_TYPE_COUNTRY,
    INPUT_TYPE_EMAIL_STATUS,
    INPUT_TYPE_SELECT_LANGUAGE_CODE,
    INPUT_TYPE_LEADSOURCE,
    INPUT_TYPE_CONTACT_TYPE,
    INPUT_TYPE_CONTACT_TITLE,
    INPUT_TYPE_USERS,
    INPUT_TYPE_CURRENCY,
    INPUT_DAY_OF_WEEK,
    INPUT_SELECT_WITH_DEFAULT,
    INPUT_DOUBLE,
    INPUT_INTEGER,
    INPUT_PERCENT,
    INPUT_DRILL_DOWN,
    INPUT_MONTH,
    INPUT_CUSTOM_EMAIL,
    INPUT_YEAR,
    INPUT_STATE_SELECT,
    INPUT_TEXT_AREA,
    INPUT_DATETIME,
} from './decisionDiamond.util';

export default {
    props: {
        ruleObject: {
            type: Object,
            required: true,
        },
        ruleValue: {
            type: Object,
            required: true,
        },
        ruleMapKey: {
            type: String,
            required: true,
        },
        valueMapKey: {
            type: Number,
            required: true,
        },
        isFirstElement: {
            type: Boolean,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        numRuleValues: {
            type: Number,
            required: true,
        },
        ddKeys: {
            type: Object,
            required: true,
        },
        inputTypeValue: {
            type: String,
            required: true,
        },
        mayOrValue: {
            type: Boolean,
            required: false,
        },
        mayAndValue: {
            type: Boolean,
            required: false,
        },
    },
    emits: ['update-rule-value', 'delete-rule-value', 'add-rule-value'],
    data() {
        return {
            multiselectInputFieldData: [],
            multiselectInputFieldOptions: [],
            selectedDatePicker: '',
            selectedInputTextField: '',
            comparison: '',
            COMPARISON_OPTIONS,
        };
    },
    computed: {
        allowAdd() {
            return !(this.isFirstElement || this.selectedMultiselectInputField?.length);
        },
        selectedMultiselectInputField: {
            set(newValue) {
                if (!newValue || newValue.includes(undefined)) {
                    return;
                }

                if (newValue.length) {
                    const updatedValue = newValue.shift();

                    this.multiselectInputFieldData = [{ ...updatedValue }];

                    const { value } = updatedValue;

                    if (value === 'delete') {
                        this.deleteRuleValueEvent();

                        return;
                    }

                    this.$emit('update-rule-value', {
                        ruleValue: {
                            ...this.ruleValue,
                            value,
                            id: this.valueMapKey,
                        },
                    });
                }
            },
            get() {
                return this.multiselectInputFieldData;
            },
        },
        isHidden() {
            return this.inputTypeValue === INPUT_TYPE_HIDDEN;
        },
        isDatePicker() {
            return this.inputTypeValue === INPUT_TYPE_DATE
                || this.inputTypeValue === INPUT_DATETIME;
        },
        isInputTextField() {
            return this.inputTypeValue === INPUT_TYPE_CITY
                || this.inputTypeValue === INPUT_TYPE_STRING
                || this.inputTypeValue === INPUT_TYPE_NAME
                || this.inputTypeValue === INPUT_TYPE_EMAIL
                || this.inputTypeValue === INPUT_TYPE_CURRENCY
                || this.inputTypeValue === INPUT_TYPE_PHONE
                || this.inputTypeValue === INPUT_TYPE_STATE
                || this.inputTypeValue === INPUT_TYPE_URL
                || this.inputTypeValue === INPUT_DOUBLE
                || this.inputTypeValue === INPUT_INTEGER
                || this.inputTypeValue === INPUT_PERCENT
                || this.inputTypeValue === INPUT_CUSTOM_EMAIL
                || this.inputTypeValue === INPUT_YEAR
                || this.inputTypeValue === INPUT_TEXT_AREA;
        },
        isMultiselectInputField() {
            return this.inputTypeValue === INPUT_TYPE_COUNTRY
                || this.inputTypeValue === INPUT_TYPE_EMAIL_STATUS
                || this.inputTypeValue === INPUT_TYPE_SELECT_LANGUAGE_CODE
                || this.inputTypeValue === INPUT_TYPE_LEADSOURCE
                || this.inputTypeValue === INPUT_TYPE_USERS
                || this.inputTypeValue === INPUT_TYPE_CONTACT_TYPE
                || this.inputTypeValue === INPUT_TYPE_CONTACT_TITLE
                || this.inputTypeValue === INPUT_TYPE_AUTOCOMPLETE
                || this.inputTypeValue === INPUT_DAY_OF_WEEK
                || this.inputTypeValue === INPUT_SELECT_WITH_DEFAULT
                || this.inputTypeValue === INPUT_DRILL_DOWN
                || this.inputTypeValue === INPUT_MONTH
                || this.inputTypeValue === INPUT_STATE_SELECT;
        },
        showOrValues() {
            return this.mayOrValue && (this.comparison === COMPARISON_OPTIONS.OR || this.numRuleValues === 1);
        },
        showAndValues() {
            return this.mayAndValue && (this.comparison === COMPARISON_OPTIONS.AND || this.numRuleValues === 1);
        },
    },
    async mounted() {
        if (!this.isHidden) {
            await this.initializeInputAttributes();
        }
    },
    methods: {
        async onBlurSaveInputTextField() {
            this.$emit('update-rule-value', {
                ruleValue: {
                    id: this.valueMapKey,
                    ruleId: this.ruleValue.ruleId,
                    value: this.selectedInputTextField,
                },
            });
        },
        async deleteRuleValueEvent() {
            const ruleValueId = this.valueMapKey;
            const { rule } = await deleteRuleValue(ruleValueId, this.ddKeys);

            this.$emit('delete-rule-value', rule);
            this.selectedInputTextField = '';
            this.selectedMultiselectInputField = [];
        },
        async addRuleValueEvent(comparisonType) {
            const payload = {
                comparison: comparisonType,
                ...this.ddKeys,
            };
            const { ruleId } = this.ruleValue;

            const { rule } = await addRuleValue(ruleId, payload);

            this.$emit('add-rule-value', rule);
        },
        onDatePickerInput() {
            if (this.selectedDatePicker) {
                this.$emit('update-rule-value', {
                    ruleValue: {
                        ...this.ruleValue,
                        value: convertFromDpToString(this.selectedDatePicker),
                        id: this.valueMapKey,
                    },
                });
            }
        },
        async initializeInputAttributes() {
            const {
                field,
                comparison,
            } = this.ruleObject;

            this.comparison = comparison;

            const { value: valueField, ruleId } = this.ruleValue;

            const fieldId = `value${this.ruleMapKey}_${ruleId}`;
            const fieldValue = valueField;
            const fieldKey = field;
            const { map } = await getInputAttributes({
                fieldId,
                fieldValue,
                fieldKey,
                ...this.ddKeys,
            });

            const {
                selectedValue,
                items = {},
                objectType,
                firstOptions,
                drillDownOptions,
            } = map;

            if (Object.keys(items).length > 0) {
                const { selectOptions } = items;

                if (selectOptions) {
                    this.multiselectInputFieldOptions = Object.entries(selectOptions).map(([key, text]) => {
                        return {
                            value: key,
                            label: text,
                        };
                    });

                    if (this.ruleValue.value) {
                        this.selectedMultiselectInputField = [this.multiselectInputFieldOptions.find((v) => v.value === this.ruleValue.value)];
                    }
                } else {
                    this.multiselectInputFieldOptions = Object.entries(items).map(([key, text]) => {
                        return {
                            value: key,
                            label: text,
                        };
                    });

                    if (this.ruleValue.value) {
                        this.selectedMultiselectInputField = [this.multiselectInputFieldOptions.find((v) => v.value === this.ruleValue.value)];
                    }
                }
            } else if (this.isDatePicker) {
                this.selectedDatePicker = (selectedValue === 'null' || selectedValue == null) ? '' : convertFromStringToDp(selectedValue);
            } else if (this.isInputTextField) {
                this.selectedInputTextField = (selectedValue === 'null' || selectedValue == null) ? '' : selectedValue;
            } else if (this.isMultiselectInputField) {
                if (objectType === 'drilldownOption') {
                    this.multiselectInputFieldOptions = Object.keys(firstOptions).map((key) => {
                        const options = Object.keys(drillDownOptions[key]).map((optionKey) => ({
                            value: optionKey,
                            label: drillDownOptions[key][optionKey],
                        }));

                        return {
                            label: firstOptions[key],
                            options,
                        };
                    });

                    if (this.ruleValue.value && this.multiselectInputFieldOptions.length > 0) {
                        const foundOption = this.multiselectInputFieldOptions.reduce((acc, group) => {
                            if (acc) return acc;
                            const option = group.options.find((opt) => opt.value === this.ruleValue.value);

                            return option || acc;
                        }, null);

                        this.selectedMultiselectInputField = foundOption ? [foundOption] : [];
                    }
                } else if (objectType === 'user') {
                    const response = await getAllUsers();

                    if (response && response.map) {
                        const { map: userMap } = response;

                        this.multiselectInputFieldOptions = Object.entries(userMap).map(([value, label]) => ({
                            value,
                            label,
                        }));

                        if (this.ruleValue.value && this.multiselectInputFieldOptions.length > 0) {
                            this.selectedMultiselectInputField = [this.multiselectInputFieldOptions.find((v) => {
                                return v.value === this.ruleValue.value;
                            })];
                        }
                    } else {
                        this.multiselectInputFieldOptions = [];
                    }
                } else if (this.ruleValue.value && this.multiselectInputFieldOptions.length > 0) {
                    this.selectedMultiselectInputField = [this.multiselectInputFieldOptions.find((v) => {
                        return v.value === this.ruleValue.value;
                    })];
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.dd-rule-value-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $spacing-125;
}
.may-or-and-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-left: $spacing-125;
    width: px-to-rem(130)
}

.may-or-label, .may-and-label {
    font-weight: bold;
    color: $color-blue;
    padding: 0 $spacing-125 $spacing-250 0;
    cursor: pointer;
}

.comparison-label {
    padding: 0 $spacing-125 $spacing-250 0;
    text-transform: lowercase;
}

.icon-x-delete-rule-value {
    cursor: pointer;
    padding-top: $spacing-125;
}

.icon-x-delete-rule-value-input {
    cursor: pointer;
}

.ds-multiselect-wrapper:deep .add {
    span {
        color: red;
    }
    svg {
        display: none;
    }
}

.ds-multiselect-wrapper, .ds-datepicker-wrapper, .ds-input-field-wrapper {
    margin-right: $spacing-125;
    min-width: 10rem;
    max-width: 100%;

    .group-label {
        font-weight: bold;
    }

    .option-label {
        margin-left: $spacing-125;
    }
}

.menu-item {
    &.isDeleteOption {
        color: red;

        .DsIcon {
            color: red;
        }
    }
}
</style>

<style lang="scss">
.dd-rule-value-row {
    .multiselect.multiple .multiselect-tags input {
        padding: 0;
    }

    div > div.multiselect-tags > span > input,
    .multiselect.active .input-container .search-input {
        width: 0;
    }

    .dropdown-menu {
        min-width: 150%;
    }

    .ds-datepicker-wrapper .date-picker-container > label {
        padding-top: 0;
    }
}
</style>
